import { PropsWithChildren } from 'react';

import Icons, { IconName } from '@/core/components/Icons';
import Modal from '@/core/components/Modal';
import UserDomain from '@/core/lib/new-architecture/domain/user.domain';
import Store from '@/core/lib/new-architecture/store';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import { ActivityAlias } from '@/core/types/profile';

const ns = 'profile.profile-public';

type SuperUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  user: UserDomain;
};
export const SuperUserModal: React.FC<SuperUserModalProps> = ({ isOpen = true, onClose, user }) => {
  const { t } = useTranslationContext([ns]);
  const { data: activitiesPoints, isLoading } = Store.activities.useActivities();

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnEscape>
      <div aria-labelledby="header-content" className="flex w-full items-center justify-end">
        <button type="button" onClick={onClose} aria-label={t('close', { ns: 'common' })} className="flex size-8 shrink-0 items-center justify-center rounded-full hover:bg-bg-button-ghost-hover">
          <Icons icon="close" size="24" />
        </button>
      </div>

      <div className="text-caption-primary space-y-5">
        <div className="space-y-2">
          <div className="text-body-primary grid grid-cols-[auto_1fr] gap-1 font-semibold">
            {award}
            <p>{t('status.super-user.modal.title', { ns })}</p>
          </div>
          <p className="text-content-placeholder">{t('status.super-user.modal.subtitle', { ns })}</p>
        </div>

        {/* hint */}
        <div className="space-y-2 rounded-2 bg-brand-alt-50 p-3">
          <div className="grid grid-cols-[auto_1fr] items-center gap-2 font-semibold">
            <div className="text-content-secondary">
              <Icons icon="clock" color="current" />
            </div>
            <p>{t('status.super-user.modal.hint.title', { ns })}</p>
          </div>
          <p className="text-content-placeholder"> {t('status.super-user.modal.hint.content', { ns, superUserThreshold: user.activityPoints.points.superUserThreshold })}</p>
        </div>

        {/* legend */}
        <div className="space-y-2">
          <p className="font-semibold">{t('status.super-user.modal.legend.title', { ns })}</p>
          <div className="grid grid-cols-2 gap-2">
            <Legend className="bg-green-50" dotClassName="bg-green-700">
              {t('status.super-user.modal.legend.not-exp-soon', { ns })}
            </Legend>
            <Legend className="bg-orange-50" dotClassName="bg-orange-500">
              {t('status.super-user.modal.legend.exp-soon', { ns })}
            </Legend>
            <Legend className="bg-neutral-0" dotClassName="bg-bg-secondary">
              {t('status.super-user.modal.legend.missing', { ns })}
            </Legend>
          </div>
        </div>

        {/* actions */}
        <div className="space-y-3">
          <p className="font-semibold">{t('status.super-user.modal.actions.title', { ns })}</p>
          <div className="space-y-2">
            {isLoading && <Icons icon="spinner" className="mx-auto" color="primary" />}
            {(['userpic', 'interest', 'login', 'donation', 'recieved', 'given'] as ActivityAlias[]).map(
              activity =>
                activitiesPoints?.[activity] && (
                  <Action
                    key={activity}
                    icon={activity}
                    title={t(`status.super-user.modal.actions.${activity}.title`, { ns })}
                    content={t(`status.super-user.modal.actions.${activity}.content`, { ns })}
                    points={activitiesPoints?.[activity]}
                  />
                ),
            )}

            <Hint />
          </div>
        </div>
      </div>
    </Modal>
  );
};

const Legend: React.FC<PropsWithChildren<{ className: string; dotClassName: string }>> = ({ dotClassName, className, children }) => (
  <div className={`flex items-center gap-2 rounded-1 p-2 ${className}`}>
    <Dot className={dotClassName} />
    {children}
  </div>
);

const Dot: React.FC<{ className: string }> = ({ className }) => <div className={`size-3 rounded-full ${className}`} />;

const Hint = () => {
  const { t } = useTranslationContext([ns]);

  return (
    <div className="grid grid-cols-[auto_1fr] gap-2 rounded-1 border border-mandarine bg-mandarine-0 p-1 text-mandarine-900">
      <div className="grid size-10 place-items-center rounded-full bg-mandarine-100 text-mandarine-700">
        <Icons icon="clock" color="current" />
      </div>
      <div className="">
        <p className="text-caption-primary font-semibold">{t('status.super-user.modal.tip.title', { ns })}</p>
        <p className="text-caption-secondary">{t('status.super-user.modal.tip.content', { ns })}</p>
      </div>
    </div>
  );
};

const Action: React.FC<{ title: string; content: string; points: number; icon: ActivityAlias }> = ({ title, content, points, icon }) => {
  const { t } = useTranslationContext([ns]);

  const iconMap: { [key in ActivityAlias]?: IconName } = {
    userpic: 'user',
    interest: 'tag',
    login: 'calendar',
    donation: 'gift',
    recieved: 'check-circle',
    given: 'pocket',
  };
  const iconName = iconMap[icon];

  return (
    <div className="grid grid-cols-[auto_1fr] gap-2 rounded-1 border border-neutral-100 p-1">
      <div className="grid size-10 place-items-center rounded-full bg-brand-alt-50 text-content-secondary">{iconName && <Icons icon={iconName} color="current" />}</div>
      <div className="">
        <div className="flex justify-between gap-2">
          <p className="text-caption-primary font-semibold">{title}</p>
          <p className="text-caption-secondary rounded-[6px] bg-bg-button-primary-default p-1 text-[8px] font-semibold leading-[10px] text-content-button-primary">
            {t('status.super-user.modal.actions.points', { ns, count: points })}
          </p>
        </div>
        <p className="text-caption-secondary text-content-placeholder">{content}</p>
      </div>
    </div>
  );
};

const award = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_18207_4063)">
      <path
        d="M17.75 8C17.75 4.82436 15.1756 2.25 12 2.25C8.82436 2.25 6.25 4.82436 6.25 8C6.25 11.1756 8.82436 13.75 12 13.75C13.1182 13.75 14.161 13.4292 15.0439 12.877C15.0867 12.8451 15.1318 12.8164 15.1787 12.79C16.728 11.7599 17.75 9.99994 17.75 8ZM14.7793 15.7676C13.9107 16.0784 12.9755 16.25 12 16.25C11.0246 16.25 10.0893 16.0793 9.2207 15.7686L8.58008 20.5928L11.3564 18.9277C11.7523 18.6902 12.2477 18.6902 12.6436 18.9277L15.4189 20.5928L14.7793 15.7676ZM20.25 8C20.25 10.6176 19.0293 12.9486 17.1279 14.46L18.2393 22.8359C18.3022 23.3115 18.0875 23.7815 17.6865 24.0449C17.2854 24.3084 16.768 24.3192 16.3564 24.0723L12 21.458L7.64355 24.0723C7.23196 24.3192 6.71465 24.3085 6.31348 24.0449C5.91248 23.7815 5.69776 23.3116 5.76074 22.8359L6.87207 14.4609C4.97038 12.9496 3.75 10.6179 3.75 8C3.75 3.44365 7.44365 -0.249999 12 -0.25C16.5563 -0.25 20.25 3.44365 20.25 8Z"
        fill="#4B4DED"
      />
    </g>
    <defs>
      <clipPath id="clip0_18207_4063">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
